import React, { FC, useCallback, useState } from "react";
import clsx from "clsx";
import { queryCache } from "react-query";
import { useHistory, useRouteMatch } from "react-router";
import Divider from "@material-ui/core/Divider";
import makeStyles from "@material-ui/core/styles/makeStyles";
import Button from "../../../../components/Button";
import { Routes } from "../../../../constants/routes";
import { urlReplacer } from "../../../../utils";
import { useWizard } from "../../../wizard/context";
import { useApplicationConfig } from "../../modals/hooks/useApplicationConfig";
import { IStudentApplicationCardProps } from "../../types";
import { ApplicationStatusInfo } from "./ApplicationStatusInfo";
import { UserRole } from "../../../user/enums/UserRole";
import { useAuth } from "../../../auth/hooks/useAuth";
import FontIcon from "../../../../components/FontIcon";
import WeduTooltip from "../../../../components/WeduTooltip/intex";
import { ActionButton } from "./ActionButtons";
import { getApplicationCancellationInfo } from "../../helpers/getApplicationCancellationInfo";
import { CardField } from "../../../../components/CardField/CardField";
import { ApplicationCardHeader } from "./ApplicationCardHeader";
import { Note } from "./Note";

const useStyles = makeStyles((theme) => ({
  card: {
    width: 732,
    height: 239,
    background: "#FFFFFF",
    boxShadow: "0px 0px 8px #C2C9D682",
    borderRadius: 15,
    "& + &": {
      marginTop: 20,
    },
  },
  header: {
    height: 90,
    borderBottom: "1px solid #DBDBDB;",
    padding: 15,
  },
  centerContent: {
    height: 109,
    borderBottom: "1px solid #DBDBDB;",
    padding: "5px 5px",
    display: "grid",
    columnGap: 2,
    gridTemplateColumns: "1fr 1fr 1fr",
  },
  footer: {
    height: 40,
    padding: "15px 15px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  statusInfoNoteContainer: {
    display: "flex",
    alignItems: "center",
  },
  disabled: {
    opacity: 0.5,
    background: "#f8f9fb",
    pointerEvents: "none",
  },
  actionPayButtonsContainer: {
    display: "flex",
    alignItems: "center",
  },
  divider: {
    alignSelf: "auto",
    backgroundColor: "#DBDBDB",
    height: "24px",
    margin: "0px 8px",
    width: "1px",
  },
  weduTooltip: {
    height: 16,
    marginLeft: 5,
  },
  warningIcon: {
    color: theme.palette.error.main,
  },
  payButton: {
    fontSize: 14,
    fontWeight: 500,
    width: 70,
    height: 30,
    padding: "unset !important",
  },
}));

export const StudentApplicationCard: FC<IStudentApplicationCardProps> = ({
  application,
  studentId,
}) => {
  const classes = useStyles();

  const { user } = useAuth();
  const { url } = useRouteMatch();
  const currentPath = url.split("/application")[0];

  const history = useHistory();
  const { openWizard } = useWizard();

  const [expandedCardFieldId, setExpandedCardFieldId] = useState(null);

  const {
    getViewApplicationConfig,
    getCancelConfig,
    getEditApplicationConfig,
    getPayForApplicationConfig,
  } = useApplicationConfig();

  const expandHandler = useCallback((id) => {
    setExpandedCardFieldId((prev) => {
      return prev === id ? null : id;
    });
  }, []);

  const closeCard = () => setExpandedCardFieldId(null);

  const isAdminOrSupport = [
    UserRole.Owner,
    UserRole.Admin,
    UserRole.Support,
  ].includes(user.role);
  const redirectToMessagesPage = () => {
    if (user.role === UserRole.Student) {
      history.push(
        urlReplacer(`/0/${Routes.USER_MESSAGES}`, {
          userRole: UserRole.School,
          recipientId: application.schoolId,
        }),
      );
    } else if (isAdminOrSupport) {
      history.push(
        urlReplacer(
          `${currentPath}/${Routes.MESSAGES}?userRole=${UserRole.School}&recipientId=${application.schoolId}`,
          {
            id: studentId,
          },
        ),
      );
    } else {
      history.push(
        urlReplacer(
          `/0/${Routes.STUDENT}/${Routes.MESSAGES}?userRole=${UserRole.School}&recipientId=${application.schoolId}`,
          {
            id: studentId,
          },
        ),
      );
    }
  };

  const applyViewApplication = async (id, educationType) => {
    await openWizard(getViewApplicationConfig(id, educationType));
  };

  const applyEditApplication = async (id, educationType) => {
    await openWizard(getEditApplicationConfig(id, educationType));
  };

  const cancelApplication = async (id, educationType) => {
    if (await openWizard(getCancelConfig(id, educationType))) {
      await queryCache.invalidateQueries();
    }
  };

  const payForApplication = async (id, educationType) => {
    if (await openWizard(getPayForApplicationConfig(id, educationType))) {
      await queryCache.invalidateQueries();

      const billingRoute =
        user.role === UserRole.Student
          ? `/0/${Routes.BILLING}`
          : urlReplacer(`/0/${Routes.STUDENT}/${Routes.BILLING}`, {
              id: studentId,
            });

      history.push(billingRoute);
    }
  };

  const { note, isDisabled, warningIconTooltipText } =
    getApplicationCancellationInfo(application);

  return (
    <div className={classes.card}>
      <div className={clsx(classes.header, isDisabled && classes.disabled)}>
        <ApplicationCardHeader content={application.headerContent} />
      </div>
      <div
        className={clsx(classes.centerContent, isDisabled && classes.disabled)}
      >
        {application.centerContent.map((cardField) => (
          <CardField
            key={cardField.id}
            id={cardField.id}
            Icon={cardField.icon}
            title={cardField.title}
            PreviewContent={cardField.previewContent}
            ExpandedContent={cardField.expandedContent}
            onExpand={expandHandler}
            onClickAway={closeCard}
            isExpanded={expandedCardFieldId === cardField.id}
          />
        ))}
      </div>
      <div className={classes.footer}>
        <div className={classes.statusInfoNoteContainer}>
          <ApplicationStatusInfo status={application.status} />
          {warningIconTooltipText && (
            <WeduTooltip
              title={warningIconTooltipText}
              className={classes.weduTooltip}
            >
              <FontIcon
                className={classes.warningIcon}
                name={"warning"}
                fontSize={"small"}
              />
            </WeduTooltip>
          )}
          {note && (
            <>
              <Divider className={classes.divider} orientation={"vertical"} />
              <Note note={note} />
            </>
          )}
        </div>
        <div className={classes.actionPayButtonsContainer}>
          <ActionButton
            goToMessages={redirectToMessagesPage}
            messageTooltip={"Go to Messages"}
            viewApp={() =>
              applyViewApplication(application.id, application.schoolType)
            }
            viewTooltip={"View application"}
            editApp={() =>
              applyEditApplication(application.id, application.schoolType)
            }
            editTooltip={"Edit application"}
            disableEdit={isDisabled}
            cancelApp={() =>
              cancelApplication(application.id, application.schoolType)
            }
            cancelTooltip={"Cancel application"}
            disableCancel={isDisabled}
            redirect={() =>
              history.push(
                urlReplacer(`/0/${application.redirectRoute}`, {
                  typeId: application.schoolType,
                  id: application.entityId,
                }),
              )
            }
            redirectTooltip={`Go to the ${application.entityName} page`}
          />
          <Divider className={classes.divider} orientation={"vertical"} />
          <Button
            onClick={() =>
              payForApplication(application.id, application.schoolType)
            }
            type="submit"
            color="secondary"
            variant="contained"
            className={classes.payButton}
            disabled={isDisabled}
          >
            Pay
          </Button>
        </div>
      </div>
    </div>
  );
};
